import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft, Clock, MessageSquare, Paperclip, History, CheckCircle, XCircle,
    X, Download, FileIcon, Image, Film, Printer, BookOpen, UserCheck, Loader2,
    Sparkles, Eye, Wrench, AlertTriangle, CalendarClock, Pencil, Archive, Trash2,
    Wand2, ChevronDown, ChevronLeft, Phone, Smartphone, Mail,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import * as Icons from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { StatusBadge, PriorityBadge } from "../shared/Badge";
import { UserAvatar, UserDisplay } from "../shared/UserAvatar";
import { PageLoader } from "../shared/LoadingSpinner";
import { EmptyState } from "../shared/EmptyState";
import {
    formatDateTime, formatRelativeTime, getTicketTypeConfig, STATUSES, PRIORITIES, TICKET_TYPES, getValidTransitions,
} from "../../lib/utils";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import TimeTracker from "./TimeTracker";
import LinkedTickets from "./LinkedTickets";
import { TicketComments } from "../ticket/TicketComments";
import { TicketAttachments } from "../ticket/TicketAttachments";
import { LinkDocDialog } from "../shared/LinkDocDialog";
import { TicketChecklists } from "../ticket/TicketChecklists";
import { TicketSolutions } from "../ticket/TicketSolutions";
import { TicketPrintView } from "../ticket/TicketPrintView";
import { TicketApprovalBanner, TicketApprovalsSidebar } from "../ticket/TicketApprovals";

function DeadlineBadge({ info }) {
    if (!info || !info.has_deadline) return null;
    const { is_overdue, remaining_seconds, remaining_label } = info;
    if (is_overdue) {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700" data-testid="deadline-badge"><AlertTriangle className="w-3 h-3" />{remaining_label}</span>;
    }
    const isUrgent = remaining_seconds != null && remaining_seconds < 86400;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${isUrgent ? "bg-orange-100 text-orange-700" : "bg-green-100 text-green-700"}`} data-testid="deadline-badge">
            <CalendarClock className="w-3 h-3" />{remaining_label}
        </span>
    );
}

function DeadlineSection({ ticket, isAgentOrAdmin, onUpdate, currentUser }) {
    const [editing, setEditing] = useState(false);
    const [newDeadline, setNewDeadline] = useState("");
    const info = ticket.deadline_info;

    const handleSave = async () => {
        try {
            await api.updateTicket(ticket.id, { deadline: newDeadline ? new Date(newDeadline).toISOString() : null }, currentUser?.id);
            toast.success("Date limite mise à jour");
            setEditing(false);
            onUpdate();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        }
    };

    const sourceLabel = info?.deadline_source === "user" ? "Défini par l'utilisateur" : info?.deadline_source === "agent" ? "Défini par l'agent" : info?.deadline_source === "auto" ? "Automatique" : "";

    return (
        <div data-testid="deadline-detail-section">
            <label className="form-label flex items-center gap-2">
                Date limite
                {isAgentOrAdmin && !editing && (
                    <button type="button" onClick={() => { setEditing(true); setNewDeadline(info?.deadline ? info.deadline.slice(0, 16) : ""); }} className="text-blue-500 hover:text-blue-700" data-testid="edit-deadline-btn">
                        <Pencil className="w-3 h-3" />
                    </button>
                )}
            </label>
            {editing ? (
                <div className="flex items-center gap-2">
                    <input type="datetime-local" value={newDeadline} onChange={(e) => setNewDeadline(e.target.value)} className="px-2 py-1 text-sm border rounded" data-testid="edit-deadline-input" />
                    <Button size="sm" onClick={handleSave} data-testid="save-deadline-btn">OK</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>X</Button>
                </div>
            ) : info?.has_deadline ? (
                <div>
                    <p className="text-sm">{formatDateTime(info.deadline)}</p>
                    <DeadlineBadge info={info} />
                    {sourceLabel && <p className="text-xs text-gray-400 mt-1">{sourceLabel}</p>}
                </div>
            ) : (
                <p className="text-sm text-gray-400">Pas de date limite</p>
            )}
        </div>
    );
}
import { TicketDynamicFields } from "../ticket/TicketDynamicFields";
import { toast } from "sonner";

function PdfPreview({ attachmentId, filename }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.default
            .get(`/attachments/${attachmentId}/preview`, { responseType: "blob" })
            .then((res) => {
                if (cancelled) return;
                const url = URL.createObjectURL(res.data);
                setBlobUrl(url);
            })
            .catch(() => {
                if (!cancelled) setError("Impossible de charger le PDF");
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => {
            cancelled = true;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attachmentId]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="pdf-loading">
                <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                <p className="text-sm text-gray-500">Chargement du PDF...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <FileIcon className="w-12 h-12 text-red-300 mx-auto mb-3" />
                <p className="text-sm text-red-500">{error}</p>
            </div>
        );
    }

    return (
        <iframe
            src={blobUrl}
            title={filename}
            className="w-full h-[75vh] rounded-md border bg-white"
            data-testid="preview-pdf"
        />
    );
}

function isPlaceholderEmail(email) {
    return !email || email.includes("@no-email.local");
}

function ContactInfo({ user }) {
    if (!user) return null;
    const email = !isPlaceholderEmail(user.email) ? user.email : null;
    const fixed = user.phone_fixed;
    const mobile = user.phone_mobile;
    if (!email && !fixed && !mobile) return null;
    return (
        <div className="mt-1.5 space-y-0.5 pl-10" data-testid="contact-info">
            {email && (
                <a href={`mailto:${email}`} className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline" data-testid="contact-email">
                    <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />{email}
                </a>
            )}
            {fixed && (
                <a href={`tel:${fixed.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-blue-600" data-testid="contact-phone-fixed">
                    <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />{fixed}
                </a>
            )}
            {mobile && (
                <a href={`tel:${mobile.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-blue-600" data-testid="contact-phone-mobile">
                    <Smartphone className="w-3 h-3 text-gray-400 flex-shrink-0" />{mobile}
                </a>
            )}
        </div>
    );
}

function PositionBadges({ user }) {
    const { positions } = usePositions();
    if (!user?.position_ids?.length) return null;
    const names = user.position_ids.map((pid) => positions.find((p) => p.id === pid)?.name).filter(Boolean);
    if (!names.length) return null;
    return (
        <div className="flex flex-wrap gap-1 mt-1 pl-10" data-testid="position-badges">
            {names.map((name) => (
                <span key={name} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                    {name}
                </span>
            ))}
        </div>
    );
}

// Simple hook to load positions once
function usePositions() {
    const [positions, setPositions] = React.useState([]);
    React.useEffect(() => {
        api.getPositions().then(setPositions).catch(() => {});
    }, []);
    return { positions };
}

export function TicketDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser, getUserById, users, hasPermission, departments, wsClient, supervisedDeptIds, isTeamSupervisor } = useApp();

    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previewAtt, setPreviewAtt] = useState(null);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkSaving, setLinkSaving] = useState(false);

    // Linked tickets — cascade close dialog
    const [cascadeDialog, setCascadeDialog] = useState(null);
    const [cascadeSelected, setCascadeSelected] = useState({});
    // 10A — Resolution rejection state
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");

    // Admin inline edit states
    const [editingTitle, setEditingTitle] = useState(false);
    const [editTitle, setEditTitle] = useState("");
    const [editingDesc, setEditingDesc] = useState(false);
    const [editDesc, setEditDesc] = useState("");
    const [editingDynamic, setEditingDynamic] = useState(false);
    const [editDynamic, setEditDynamic] = useState({});
    const [editingBeneficiary, setEditingBeneficiary] = useState(false);

    // Define loadTicket BEFORE useEffects that depend on it (avoid TDZ error)
    const loadTicket = useCallback(async () => {
        try {
            setLoading(true);
            const data = await api.getTicket(id, currentUser?.id);
            setTicket(data);
        } catch {
            toast.error("Erreur lors du chargement du ticket");
        } finally {
            setLoading(false);
        }
    }, [id, currentUser?.id]);

    useEffect(() => {
        loadTicket();
    }, [loadTicket]);

    // 10B — WebSocket: auto-refresh ticket detail on relevant events
    useEffect(() => {
        if (!wsClient || !id) return;
        const handleUpdate = (data) => {
            if (data.ticket_id === id) loadTicket();
        };
        const handleComment = (data) => {
            if (data.ticket_id === id) loadTicket();
        };
        const unsub1 = wsClient.on("ticket_updated", handleUpdate);
        const unsub2 = wsClient.on("comment_added", handleComment);
        return () => { unsub1(); unsub2(); };
    }, [wsClient, id, loadTicket]);

    // Dynamic page title
    useEffect(() => {
        if (ticket?.ticket_number) {
            document.title = `${ticket.ticket_number} - Helpdesk`;
        }
        return () => { document.title = "Helpdesk - EggTelsa"; };
    }, [ticket?.ticket_number]);

    // Load AI status for agents/admins/coordinators
    useEffect(() => {
        const canUseAI = currentUser?.role === "admin" || currentUser?.role === "agent" || currentUser?.role === "coordinator";
        if (canUseAI) {
            api.getAiStatus().then((d) => setAiEnabled(d.enabled)).catch(() => {});
        }
    }, [currentUser?.role]);

    // ===== CASCADE CLOSE =====
    const CLOSEABLE_STATUSES = ["new", "open", "in_progress", "pending_approval"];

    const handleStatusChange = async (newStatus) => {
        if (newStatus === "resolved" && ticket?.links?.length > 0) {
            const openChildren = ticket.links
                .filter((l) => CLOSEABLE_STATUSES.includes(l.linked_ticket?.status))
                .map((l) => ({ ...l.linked_ticket, linkId: l.id }));
            if (openChildren.length > 0) {
                const selected = {};
                openChildren.forEach((c) => { selected[c.id] = true; });
                setCascadeSelected(selected);
                setCascadeDialog({ openChildren, pendingStatus: newStatus });
                return;
            }
        }
        try {
            await api.updateTicket(id, { status: newStatus }, currentUser.id);
            toast.success("Statut mis à jour");
            loadTicket();
        } catch { toast.error("Erreur lors de la mise à jour"); }
    };

    const handleCascadeConfirm = async () => {
        const { openChildren, pendingStatus } = cascadeDialog;
        try {
            await api.updateTicket(id, { status: pendingStatus }, currentUser.id);
            const toClose = openChildren.filter((c) => cascadeSelected[c.id]).map((c) => c.id);
            if (toClose.length > 0) {
                await api.closeChildren(id, { child_ticket_ids: toClose, resolution_comment: "" }, currentUser.id);
                toast.success(`Statut mis à jour + ${toClose.length} ticket(s) lié(s) fermé(s)`);
            } else {
                toast.success("Statut mis à jour");
            }
            setCascadeDialog(null);
            loadTicket();
        } catch { toast.error("Erreur lors de la mise à jour"); }
    };

    const handleCascadeSkip = async () => {
        try {
            await api.updateTicket(id, { status: cascadeDialog.pendingStatus }, currentUser.id);
            toast.success("Statut mis à jour");
            setCascadeDialog(null);
            loadTicket();
        } catch { toast.error("Erreur lors de la mise à jour"); }
    };

    const handlePriorityChange = async (newPriority) => {
        try {
            await api.updateTicket(id, { priority: newPriority }, currentUser.id);
            toast.success("Priorité mise à jour");
            loadTicket();
        } catch { toast.error("Erreur lors de la mise à jour"); }
    };

    const handleAssign = async (assigneeId) => {
        try {
            await api.assignTicket(id, assigneeId, currentUser.id);
            toast.success("Ticket assigné");
            loadTicket();
        } catch { toast.error("Erreur lors de l'assignation"); }
    };

    const handleDeleteAttachment = async (attId) => {
        if (!window.confirm("Supprimer cette pièce jointe ?")) return;
        try {
            await api.deleteAttachment(attId);
            toast.success("Pièce jointe supprimée");
            loadTicket();
        } catch { toast.error("Erreur lors de la suppression"); }
    };

    const handleAddLink = async ({ url, name }) => {
        setLinkSaving(true);
        try {
            await api.addTicketLinkAttachment(ticket.id, { url, name });
            toast.success("Lien ajouté aux pièces jointes");
            setLinkDialogOpen(false);
            loadTicket();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'ajout du lien"); }
        finally { setLinkSaving(false); }
    };

    const handleApprovalDecision = async (approvalId, status) => {
        try {
            await api.decideApproval(approvalId, status, null, currentUser.id);
            toast.success(status === "approved" ? "Approuvé" : "Rejeté");
            loadTicket();
        } catch { toast.error("Erreur lors de la décision"); }
    };

    // 10A — Resolution confirmation by user
    const handleConfirmResolution = async () => {
        try {
            await api.updateTicket(id, { status: "closed" }, currentUser.id);
            toast.success("Résolution confirmée, ticket clôturé");
            loadTicket();
        } catch { toast.error("Erreur lors de la confirmation"); }
    };

    const handleRejectResolution = async () => {
        try {
            await api.updateTicket(id, { status: "in_progress", rejection_reason: rejectionReason }, currentUser.id);
            toast.success("Résolution rejetée, ticket réouvert");
            setShowRejectForm(false);
            setRejectionReason("");
            loadTicket();
        } catch { toast.error("Erreur lors du rejet"); }
    };

    const handlePrint = () => { window.print(); };

    // Archive / Unarchive / Delete handlers
    const handleArchive = async () => {
        try {
            await api.archiveTicket(id, currentUser.id);
            toast.success("Ticket archivé");
            loadTicket();
        } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'archivage"); }
    };

    const handleUnarchive = async () => {
        try {
            await api.unarchiveTicket(id, currentUser.id);
            toast.success("Ticket désarchivé");
            loadTicket();
        } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors du désarchivage"); }
    };

    // Admin inline edit handlers
    const handleSaveTitle = async () => {
        if (!editTitle.trim()) return;
        try {
            await api.updateTicket(id, { title: editTitle.trim() }, currentUser.id);
            toast.success("Titre mis à jour");
            setEditingTitle(false);
            loadTicket();
        } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    };

    const handleSaveDescription = async () => {
        try {
            await api.updateTicket(id, { description: editDesc }, currentUser.id);
            toast.success("Description mise à jour");
            setEditingDesc(false);
            loadTicket();
        } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    };

    const handleSaveDynamicFields = async () => {
        try {
            await api.updateTicket(id, { dynamic_fields: editDynamic }, currentUser.id);
            toast.success("Champs dynamiques mis à jour");
            setEditingDynamic(false);
            loadTicket();
        } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    };

    const handleChangeBeneficiary = async (newBenId) => {
        try {
            await api.updateTicket(id, { beneficiary_id: newBenId || null }, currentUser.id);
            toast.success("Bénéficiaire mis à jour");
            setEditingBeneficiary(false);
            loadTicket();
        } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    };

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const handleDelete = async () => {
        try {
            await api.deleteTicket(id);
            toast.success("Ticket supprimé");
            navigate("/admin/tickets");
        } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de la suppression"); }
    };

    // AI Reformulation
    const [aiEnabled, setAiEnabled] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiResult, setAiResult] = useState(null); // { original, reformulated }
    const [aiEdited, setAiEdited] = useState("");

    const handleReformulate = async () => {
        setAiLoading(true);
        try {
            const result = await api.reformulateDescription({
                description: ticket.description,
                ticket_type: ticket.type,
                title: ticket.title,
            });
            setAiResult(result);
            setAiEdited(result.reformulated);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la reformulation");
        } finally {
            setAiLoading(false);
        }
    };

    const handleApplyReformulation = async () => {
        try {
            await api.updateTicket(id, { description: aiEdited }, currentUser.id);
            toast.success("Description mise à jour");
            setAiResult(null);
            loadTicket();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
        }
    };

    // Type change — 2-step wizard
    const [showTypeSelector, setShowTypeSelector] = useState(false);
    const [typeSelectorStep, setTypeSelectorStep] = useState(1);
    const [selectedCatForType, setSelectedCatForType] = useState(null);
    const [ticketCategories, setTicketCategories] = useState([]);

    useEffect(() => {
        api.getTicketCategories().then(setTicketCategories).catch(() => {});
    }, []);

    const handleTypeChange = async (newType) => {
        if (newType === ticket.type) { setShowTypeSelector(false); return; }
        try {
            await api.updateTicket(id, { type: newType }, currentUser.id);
            toast.success("Type de ticket modifié");
            setShowTypeSelector(false);
            setTypeSelectorStep(1);
            setSelectedCatForType(null);
            loadTicket();
        } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    };

    const openTypeSelector = () => {
        setTypeSelectorStep(1);
        setSelectedCatForType(null);
        setShowTypeSelector(true);
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} o`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
    };

    if (loading) return <PageLoader />;

    if (!ticket) {
        return (
            <div className="page-container">
                <EmptyState title="Ticket non trouvé" description="Ce ticket n'existe pas ou a été supprimé"
                    action={<Link to="/tickets"><Button>Retour aux tickets</Button></Link>}
                />
            </div>
        );
    }

    const typeConfig = getTicketTypeConfig(ticket.type);
    const requester = getUserById(ticket.requester_id);
    const assignee = getUserById(ticket.assigned_to_id);
    const beneficiaryUser = getUserById(ticket.beneficiary_id);
    const isAgentOrAdmin = hasPermission("agent");
    const isCoordinatorOrAbove = hasPermission("coordinator");
    const isAdmin = currentUser?.role === "admin";

    const visibleComments = (ticket.comments || []).filter((c) => !c.is_internal || isCoordinatorOrAbove);

    // Check if current user is assistant for any approver
    const assistantForDepts = departments.filter((d) => (d.assistant_ids || []).includes(currentUser?.id));
    const assistantForManagers = assistantForDepts.map((d) => d.manager_id).filter(Boolean);

    const myPendingApproval = (ticket.approvals || []).find((a) => {
        if (a.status !== "pending") return false;
        if (a.approver_id === currentUser?.id) return true;
        return assistantForManagers.includes(a.approver_id);
    });
    const isAssistantApproval = myPendingApproval && myPendingApproval.approver_id !== currentUser?.id;
    const assistantManagerName = isAssistantApproval ? (() => {
        const mgr = getUserById(myPendingApproval.approver_id);
        return mgr ? `${mgr.first_name} ${mgr.last_name}` : "";
    })() : "";
    const isInvolved = currentUser?.id === ticket.requester_id || currentUser?.id === ticket.beneficiary_id;
    // Team supervisor: user supervises the ticket's department but is not the requester/beneficiary
    const isTeamViewer = !isCoordinatorOrAbove && isTeamSupervisor && !isInvolved && supervisedDeptIds.includes(ticket.department_id);
    const showResolutionConfirmation = ticket.status === "resolved" && isInvolved && !isAgentOrAdmin;
    const firstViewedByUser = getUserById(ticket.first_viewed_by);
    const isArchived = !!ticket.archived_at;
    const isTerminal = ["closed", "cancelled", "resolved"].includes(ticket.status);
    const canArchive = isAgentOrAdmin && isTerminal && !isArchived;
    const canUnarchive = isAgentOrAdmin && isArchived;
    const canDelete = isAdmin;

    return (
        <div className="page-container" data-testid="ticket-detail">
            <Link to="/tickets" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-4">
                <ArrowLeft className="w-4 h-4" /> Retour aux tickets
            </Link>

            {/* Archived banner */}
            {isArchived && (
                <div className="flex items-center justify-between gap-3 px-4 py-3 mb-4 bg-gray-100 border border-gray-300 rounded-lg" data-testid="archived-banner">
                    <div className="flex items-center gap-2">
                        <Archive className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Ce ticket est archivé</span>
                    </div>
                    {canUnarchive && (
                        <Button size="sm" variant="outline" onClick={handleUnarchive} data-testid="unarchive-btn">
                            Désarchiver
                        </Button>
                    )}
                </div>
            )}

            {/* Header */}
            <div className="flex items-start justify-between mb-6 print:mb-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-mono text-gray-400">{ticket.ticket_number}</span>
                        <span className="text-sm text-gray-400">{typeConfig.label}</span>
                        <DeadlineBadge info={ticket.deadline_info} />
                    </div>
                    <h1 className="page-title flex items-center gap-2">
                        {editingTitle ? (
                            <div className="flex items-center gap-2 w-full" data-testid="edit-title-form">
                                <input
                                    type="text"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    className="flex-1 text-2xl font-bold border border-blue-300 rounded-lg px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    autoFocus
                                    data-testid="edit-title-input"
                                />
                                <Button size="sm" onClick={handleSaveTitle} disabled={!editTitle.trim()} data-testid="save-title-btn">OK</Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingTitle(false)}>X</Button>
                            </div>
                        ) : (
                            <>
                                {ticket.title}
                                {isAdmin && (
                                    <button type="button" onClick={() => { setEditTitle(ticket.title); setEditingTitle(true); }} className="text-blue-500 hover:text-blue-700 print:hidden" data-testid="edit-title-btn">
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </>
                        )}
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    {canArchive && (
                        <Button variant="outline" size="sm" onClick={handleArchive} className="print:hidden gap-2" data-testid="archive-btn">
                            <Archive className="w-4 h-4" /> Archiver
                        </Button>
                    )}
                    {canDelete && (
                        <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(true)} className="print:hidden gap-2 text-red-600 border-red-300 hover:bg-red-50" data-testid="delete-ticket-btn">
                            <Trash2 className="w-4 h-4" /> Supprimer
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={handlePrint} className="print:hidden" data-testid="print-ticket-btn">
                        <Printer className="w-4 h-4 mr-2" /> Imprimer
                    </Button>
                    <StatusBadge status={ticket.status} />
                    <PriorityBadge priority={ticket.priority} />
                </div>
            </div>

            {/* Approval Banner */}
            <TicketApprovalBanner approval={myPendingApproval} onDecide={handleApprovalDecision} isAssistant={isAssistantApproval} managerName={assistantManagerName} />

            {/* FIX 2 (TEST 15) — User-facing contextual status banners */}
            {!isCoordinatorOrAbove && ticket.status === "new" && (
                <div className="flex items-center gap-3 px-4 py-3 mb-4 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="status-banner-new">
                    <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    <p className="text-sm text-indigo-800">Votre demande a été enregistrée. Elle sera prise en charge prochainement par l'équipe IT.</p>
                </div>
            )}
            {!isCoordinatorOrAbove && ticket.status === "open" && (
                <div className="flex items-center gap-3 px-4 py-3 mb-4 bg-green-50 border border-green-200 rounded-lg" data-testid="status-banner-open">
                    <Eye className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-800">Votre demande a été consultée par l'équipe IT.</p>
                </div>
            )}
            {!isCoordinatorOrAbove && ticket.status === "in_progress" && (
                <div className="flex items-center gap-3 px-4 py-3 mb-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="status-banner-in-progress">
                    <Wrench className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <p className="text-sm text-blue-800">Votre demande est en cours de traitement.</p>
                </div>
            )}

            {/* 8A — Pending user banner for end users */}
            {ticket.status === "pending_user" && !isCoordinatorOrAbove && (
                <div className="flex items-center gap-3 px-4 py-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="pending-user-banner">
                    <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-800">L'équipe IT attend votre réponse. Merci de répondre en commentaire.</p>
                </div>
            )}

            {/* 10A — Resolution confirmation banner for end users */}
            {showResolutionConfirmation && (
                <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg overflow-hidden" data-testid="resolution-confirmation-banner">
                    <div className="flex items-center gap-3 px-4 py-3">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-sm font-medium text-emerald-900">Ce ticket a été marqué comme résolu</p>
                            {(() => {
                                const retainedSol = (ticket.solutions || []).find(s => s.is_retained);
                                return retainedSol ? (
                                    <p className="text-xs text-emerald-700 mt-1" data-testid="retained-solution-banner">
                                        Solution appliquée : {retainedSol.content.length > 200 ? retainedSol.content.slice(0, 200) + "..." : retainedSol.content}
                                    </p>
                                ) : null;
                            })()}
                            <p className="text-xs text-emerald-700 mt-0.5">Confirmez-vous que le problème est résolu ?</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-50"
                                onClick={() => setShowRejectForm(!showRejectForm)}
                                data-testid="reject-resolution-btn"
                            >
                                <XCircle className="w-3.5 h-3.5 mr-1.5" /> Non, réouvrir
                            </Button>
                            <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={handleConfirmResolution}
                                data-testid="confirm-resolution-btn"
                            >
                                <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Oui, clôturer
                            </Button>
                        </div>
                    </div>
                    {showRejectForm && (
                        <div className="px-4 pb-3 pt-1 border-t border-emerald-200 bg-white/50" data-testid="rejection-form">
                            <label className="text-xs font-medium text-gray-700 mb-1 block">Expliquez ce qui ne fonctionne pas encore <span className="text-red-500">*</span></label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    placeholder="Le problème persiste parce que..."
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    data-testid="rejection-reason-input"
                                />
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={handleRejectResolution}
                                    disabled={!rejectionReason.trim()}
                                    data-testid="submit-rejection-btn"
                                >
                                    Réouvrir le ticket
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Description */}
                    <div className="card">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                Description
                                {isAdmin && !editingDesc && (
                                    <button type="button" onClick={() => { setEditDesc(ticket.description || ""); setEditingDesc(true); }} className="text-blue-500 hover:text-blue-700" data-testid="edit-description-btn">
                                        <Pencil className="w-3 h-3" />
                                    </button>
                                )}
                            </h2>
                            {isCoordinatorOrAbove && aiEnabled && ticket.description && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleReformulate}
                                    disabled={aiLoading}
                                    className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50"
                                    data-testid="ai-reformulate-btn"
                                >
                                    {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                                    {aiLoading ? "Reformulation..." : "Reformuler avec l'IA"}
                                </Button>
                            )}
                        </div>
                        <div className="card-body">
                            {editingDesc ? (
                                <div data-testid="edit-description-form">
                                    <textarea
                                        value={editDesc}
                                        onChange={(e) => setEditDesc(e.target.value)}
                                        className="w-full min-h-[150px] text-sm border border-blue-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-y"
                                        autoFocus
                                        data-testid="edit-description-input"
                                    />
                                    <div className="flex items-center gap-2 mt-2">
                                        <Button size="sm" onClick={handleSaveDescription} data-testid="save-description-btn">Enregistrer</Button>
                                        <Button size="sm" variant="ghost" onClick={() => setEditingDesc(false)}>Annuler</Button>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.description || "Aucune description"}</p>
                            )}
                        </div>
                    </div>

                    {/* Dynamic Fields */}
                    {editingDynamic ? (
                        <div className="card" data-testid="edit-dynamic-fields-form">
                            <div className="card-header flex items-center justify-between">
                                <h2 className="text-sm font-semibold text-gray-900">Champs dynamiques — Édition</h2>
                            </div>
                            <div className="card-body">
                                {Object.keys(editDynamic).length === 0 ? (
                                    <p className="text-sm text-gray-400">Aucun champ dynamique à modifier</p>
                                ) : (
                                    <div className="space-y-3">
                                        {Object.entries(editDynamic).map(([key, value]) => (
                                            <div key={key}>
                                                <label className="form-label text-xs">{key}</label>
                                                {typeof value === "boolean" ? (
                                                    <select
                                                        value={value ? "true" : "false"}
                                                        onChange={(e) => setEditDynamic((prev) => ({ ...prev, [key]: e.target.value === "true" }))}
                                                        className="w-full px-3 py-1.5 text-sm border rounded-md"
                                                        data-testid={`edit-dynamic-${key}`}
                                                    >
                                                        <option value="true">Oui</option>
                                                        <option value="false">Non</option>
                                                    </select>
                                                ) : typeof value === "object" && value !== null ? (
                                                    <textarea
                                                        value={JSON.stringify(value, null, 2)}
                                                        onChange={(e) => {
                                                            try {
                                                                const parsed = JSON.parse(e.target.value);
                                                                setEditDynamic((prev) => ({ ...prev, [key]: parsed }));
                                                            } catch {
                                                                // keep raw for now
                                                            }
                                                        }}
                                                        className="w-full px-3 py-1.5 text-sm border rounded-md font-mono min-h-[80px]"
                                                        data-testid={`edit-dynamic-${key}`}
                                                    />
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={value ?? ""}
                                                        onChange={(e) => setEditDynamic((prev) => ({ ...prev, [key]: e.target.value }))}
                                                        className="w-full px-3 py-1.5 text-sm border rounded-md"
                                                        data-testid={`edit-dynamic-${key}`}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="flex items-center gap-2 mt-3">
                                    <Button size="sm" onClick={handleSaveDynamicFields} data-testid="save-dynamic-fields-btn">Enregistrer</Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingDynamic(false)}>Annuler</Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="relative">
                            {isAdmin && ticket.dynamic_fields && Object.keys(ticket.dynamic_fields).length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => { setEditDynamic({ ...ticket.dynamic_fields }); setEditingDynamic(true); }}
                                    className="absolute top-3 right-3 text-blue-500 hover:text-blue-700 z-10"
                                    data-testid="edit-dynamic-fields-btn"
                                >
                                    <Pencil className="w-3.5 h-3.5" />
                                </button>
                            )}
                            <TicketDynamicFields fields={ticket.dynamic_fields} />
                        </div>
                    )}

                    {/* Tabs: Comments, History, Attachments */}
                    <div className="card">
                        <Tabs defaultValue="comments">
                            <div className="border-b border-gray-200">
                                <TabsList className="px-4">
                                    <TabsTrigger value="comments" className="gap-2">
                                        <MessageSquare className="w-4 h-4" /> Commentaires ({visibleComments.length})
                                    </TabsTrigger>
                                    <TabsTrigger value="history" className="gap-2">
                                        <History className="w-4 h-4" /> Historique ({ticket.history?.length || 0})
                                    </TabsTrigger>
                                    <TabsTrigger value="attachments" className="gap-2">
                                        <Paperclip className="w-4 h-4" /> Pièces jointes ({ticket.attachments?.length || 0})
                                    </TabsTrigger>
                                </TabsList>
                            </div>

                            <TabsContent value="comments" className="p-4">
                                <TicketComments
                                    ticketId={id}
                                    ticket={ticket}
                                    comments={visibleComments}
                                    isAgentOrAdmin={isAgentOrAdmin}
                                    currentUser={currentUser}
                                    getUserById={getUserById}
                                    formatRelativeTime={formatRelativeTime}
                                    onRefresh={loadTicket}
                                    aiEnabled={aiEnabled}
                                />
                            </TabsContent>

                            <TabsContent value="history" className="p-4">
                                {ticket.history?.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center py-4">Aucun historique</p>
                                ) : (
                                    <div className="timeline">
                                        {ticket.history?.map((entry) => {
                                            const actor = getUserById(entry.actor_id);
                                            return (
                                                <div key={entry.id} className="timeline-item" data-testid={`history-${entry.id}`}>
                                                    <div className="timeline-dot" />
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-sm font-medium">{actor?.first_name} {actor?.last_name}</span>
                                                        <span className="text-xs text-gray-400">{formatDateTime(entry.timestamp)}</span>
                                                    </div>
                                                    <p className="text-sm text-gray-600">{entry.action.replace(/_/g, " ")}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="attachments" className="p-4">
                                <div className="flex justify-end mb-3">
                                    <Button variant="outline" size="sm" onClick={() => setLinkDialogOpen(true)} className="gap-1.5 text-sky-700 border-sky-200 hover:bg-sky-50" data-testid="add-link-attachment-btn">
                                        <Icons.Link2 className="w-3.5 h-3.5" />Ajouter un lien (OneDrive, SharePoint…)
                                    </Button>
                                </div>
                                <TicketAttachments
                                    attachments={ticket.attachments}
                                    isAgentOrAdmin={isAgentOrAdmin}
                                    getUserById={getUserById}
                                    onDelete={handleDeleteAttachment}
                                    onPreview={setPreviewAtt}
                                    formatRelativeTime={formatRelativeTime}
                                />
                                <LinkDocDialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} onSubmit={handleAddLink} submitting={linkSaving} title="Ajouter un lien en pièce jointe" />
                            </TabsContent>
                        </Tabs>
                    </div>

                    {/* Solutions */}
                    <TicketSolutions
                        ticket={ticket}
                        solutions={ticket.solutions || []}
                        onUpdate={loadTicket}
                        currentUser={currentUser}
                        isAgentOrAdmin={isAgentOrAdmin}
                        getUserById={getUserById}
                        aiEnabled={aiEnabled}
                    />

                    {/* 8C — Checklists */}
                    {(ticket.checklists?.length > 0 || isCoordinatorOrAbove) && (
                        <div className="card">
                            <div className="card-body">
                                <TicketChecklists
                                    ticket={ticket}
                                    checklists={ticket.checklists || []}
                                    onUpdate={loadTicket}
                                    currentUser={currentUser}
                                    isAgentOrAdmin={isAgentOrAdmin}
                                    getUserById={getUserById}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Quick Info */}
                    <div className="card">
                        <div className="card-body space-y-4">
                            <div>
                                <label className="form-label">Demandeur</label>
                                <UserDisplay user={requester} showEmail />
                                {requester?.user_type === "external" && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 mt-1" data-testid="requester-ext-badge">EXT</span>}
                                <ContactInfo user={requester} />
                                <PositionBadges user={requester} />
                                {/* Saisi par — only shown if different from requester */}
                                {ticket.created_by && ticket.created_by !== ticket.requester_id && (() => {
                                    const creator = getUserById(ticket.created_by);
                                    return creator ? (
                                        <p className="text-[10px] text-gray-400 mt-1" data-testid="created-by-info">
                                            Saisi par {creator.first_name} {creator.last_name}
                                        </p>
                                    ) : null;
                                })()}
                            </div>
                            {/* Bénéficiaire — always shown */}
                            <div>
                                <div className="p-2.5 bg-green-50 rounded-lg border border-green-200" data-testid="beneficiary-info">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <UserCheck className="w-3.5 h-3.5 text-green-600" />
                                        <label className="text-xs font-medium text-green-700">Bénéficiaire</label>
                                        {beneficiaryUser && beneficiaryUser.id === ticket.requester_id && (
                                            <span className="text-[9px] text-gray-400 ml-1">(demandeur)</span>
                                        )}
                                        {isAdmin && !editingBeneficiary && (
                                            <button type="button" onClick={() => setEditingBeneficiary(true)} className="text-blue-500 hover:text-blue-700 ml-auto" data-testid="edit-beneficiary-btn">
                                                <Pencil className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                    {editingBeneficiary ? (
                                        <div data-testid="edit-beneficiary-form">
                                            <UserSearchSelect
                                                users={users.filter((u) => u.is_active !== false)}
                                                departments={departments}
                                                value={ticket.beneficiary_id || null}
                                                onChange={(v) => { handleChangeBeneficiary(v); }}
                                                placeholder="Rechercher un bénéficiaire..."
                                                allowCreateExternal={false}
                                                data-testid="beneficiary-select"
                                            />
                                            <Button size="sm" variant="ghost" onClick={() => setEditingBeneficiary(false)} className="mt-1">Annuler</Button>
                                        </div>
                                    ) : beneficiaryUser ? (
                                        <>
                                            <UserDisplay user={beneficiaryUser} showEmail />
                                            {beneficiaryUser?.user_type === "external" && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 mt-1">EXT</span>}
                                            <ContactInfo user={beneficiaryUser} />
                                        </>
                                    ) : (
                                        <p className="text-xs text-gray-400">Non défini</p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="form-label">Assigné à</label>
                                {isAgentOrAdmin ? (
                                    <UserSearchSelect
                                        users={users.filter((u) => u.role === "admin" || u.role === "agent")}
                                        departments={departments}
                                        value={ticket.assigned_to_id || null}
                                        onChange={(v) => v && handleAssign(v)}
                                        placeholder="Rechercher un agent..."
                                        allowCreateExternal={false}
                                        data-testid="assignee-select"
                                    />
                                ) : <UserDisplay user={assignee} />}
                            </div>
                            <div>
                                <label className="form-label">Statut</label>
                                {isAgentOrAdmin ? (
                                    <Select value={ticket.status} onValueChange={handleStatusChange}>
                                        <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {getValidTransitions(ticket.status).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                ) : <StatusBadge status={ticket.status} />}
                            </div>
                            <div>
                                <label className="form-label">Priorité</label>
                                {isAgentOrAdmin ? (
                                    <Select value={ticket.priority} onValueChange={handlePriorityChange}>
                                        <SelectTrigger data-testid="priority-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {PRIORITIES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                ) : <PriorityBadge priority={ticket.priority} />}
                            </div>
                            {/* Type — badge + 2-step wizard */}
                            <div>
                                <label className="form-label flex items-center gap-2">
                                    Type
                                    {isAgentOrAdmin && !showTypeSelector && (
                                        <button type="button" onClick={openTypeSelector} className="text-blue-500 hover:text-blue-700" data-testid="edit-type-btn">
                                            <Pencil className="w-3 h-3" />
                                        </button>
                                    )}
                                </label>
                                {isAgentOrAdmin && showTypeSelector ? (
                                    <div className="relative" data-testid="type-selector">
                                        <div className="border rounded-lg bg-white shadow-lg overflow-hidden">
                                            {/* Header */}
                                            <div className="px-3 py-2 bg-gray-50 border-b flex items-center justify-between">
                                                {typeSelectorStep === 2 ? (
                                                    <button onClick={() => { setTypeSelectorStep(1); setSelectedCatForType(null); }} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="type-wizard-back">
                                                        <ChevronLeft className="w-3 h-3" />
                                                        Retour
                                                    </button>
                                                ) : (
                                                    <span className="text-xs font-semibold text-gray-500">Choisir une catégorie</span>
                                                )}
                                                <button onClick={() => { setShowTypeSelector(false); setTypeSelectorStep(1); setSelectedCatForType(null); }} className="text-gray-400 hover:text-gray-600" data-testid="type-wizard-cancel">
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>

                                            {/* Step 1 — Category selection */}
                                            {typeSelectorStep === 1 && (
                                                <div className="p-2 space-y-1" data-testid="type-wizard-step1">
                                                    {ticketCategories.map((cat) => {
                                                        const CatIcon = Icons[cat.icon] || Icons.Folder;
                                                        return (
                                                            <button
                                                                key={cat.id}
                                                                onClick={() => { setSelectedCatForType(cat); setTypeSelectorStep(2); }}
                                                                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-left text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors border border-transparent hover:border-blue-200"
                                                                data-testid={`type-cat-${cat.id}`}
                                                            >
                                                                <CatIcon className="w-4 h-4 opacity-60 flex-shrink-0" />
                                                                <span>{cat.name}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* Step 2 — Types in selected category */}
                                            {typeSelectorStep === 2 && selectedCatForType && (
                                                <div className="p-2 space-y-0.5 max-h-60 overflow-y-auto" data-testid="type-wizard-step2">
                                                    <p className="px-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{selectedCatForType.name}</p>
                                                    {(selectedCatForType.ticket_types || []).map((typeId) => {
                                                        const tc = TICKET_TYPES.find((t) => t.id === typeId);
                                                        if (!tc) return null;
                                                        const TypeIcon = Icons[tc.icon] || Icons.HelpCircle;
                                                        return (
                                                            <button
                                                                key={tc.id}
                                                                onClick={() => handleTypeChange(tc.id)}
                                                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md transition-colors ${tc.id === ticket.type ? "bg-blue-50 text-blue-700 font-medium ring-1 ring-blue-200" : "text-gray-700 hover:bg-gray-50"}`}
                                                                data-testid={`type-option-${tc.id}`}
                                                            >
                                                                <TypeIcon className="w-4 h-4 opacity-60 flex-shrink-0" />
                                                                {tc.label}
                                                                {tc.id === ticket.type && <span className="ml-auto text-[10px] text-blue-500">actuel</span>}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        {(() => {
                                            const TypeIcon = Icons[typeConfig.icon] || Icons.HelpCircle;
                                            return <TypeIcon className="w-4 h-4 text-gray-500" />;
                                        })()}
                                        <span className="text-sm text-gray-700 font-medium">{typeConfig.label}</span>
                                    </div>
                                )}
                            </div>
                            <DeadlineSection ticket={ticket} isAgentOrAdmin={isAgentOrAdmin} onUpdate={loadTicket} currentUser={currentUser} />
                            <div>
                                <label className="form-label">Créé le</label>
                                <p className="text-sm">{formatDateTime(ticket.created_at)}</p>
                            </div>
                            {ticket.first_response_at && (
                                <div><label className="form-label">Première réponse</label><p className="text-sm">{formatDateTime(ticket.first_response_at)}</p></div>
                            )}
                            {isCoordinatorOrAbove && ticket.first_viewed_at && (
                                <div data-testid="first-viewed-info">
                                    <label className="form-label">Première consultation</label>
                                    <p className="text-sm">{formatDateTime(ticket.first_viewed_at)}</p>
                                    {firstViewedByUser && <p className="text-xs text-gray-500">par {firstViewedByUser.first_name} {firstViewedByUser.last_name}</p>}
                                </div>
                            )}
                            {ticket.resolved_at && (
                                <div><label className="form-label">Résolu le</label><p className="text-sm">{formatDateTime(ticket.resolved_at)}</p></div>
                            )}
                        </div>
                    </div>

                    {/* Create KB Article */}
                    {isCoordinatorOrAbove && (ticket.status === "resolved" || ticket.status === "closed") && (
                        <button
                            onClick={() => {
                                const resolutionComment = (ticket.comments || []).filter((c) => !c.is_internal).reverse().find((c) => c.is_system || c.author_id !== ticket.requester_id);
                                const content = `## Problème\n\n${ticket.description || ""}\n\n## Résolution\n\n${resolutionComment?.content || "À compléter..."}`;
                                navigate(`/admin/base-de-connaissances?new=1&${new URLSearchParams({ title: ticket.title, content, type: ticket.type, source: ticket.id }).toString()}`);
                            }}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 rounded-lg text-xs font-medium transition-colors"
                            data-testid="create-kb-article-btn"
                        >
                            <BookOpen className="w-3.5 h-3.5" /> Créer un article KB
                        </button>
                    )}

                    {/* Approvals Sidebar */}
                    <TicketApprovalsSidebar approvals={ticket.approvals} getUserById={getUserById} />

                    {/* Linked Tickets — agent/admin/coordinator */}
                    {isCoordinatorOrAbove && (
                        <LinkedTickets ticketId={id} links={ticket.links || []} currentUser={currentUser} isAgentOrAdmin={isAgentOrAdmin} onRefresh={loadTicket} />
                    )}

                    {/* Time Tracking — agent/admin only */}
                    {isAgentOrAdmin && (
                        <TimeTracker ticketId={id} ticketNumber={ticket.ticket_number} currentUser={currentUser} isAgentOrAdmin={isAgentOrAdmin} />
                    )}
                </div>
            </div>

            {/* Print version — QR code + full structured document */}
            <TicketPrintView
                ticket={ticket}
                getUserById={getUserById}
                getTicketTypeConfig={getTicketTypeConfig}
                departments={departments}
            />

            {/* CASCADE CLOSE DIALOG */}
            {cascadeDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="cascade-close-overlay">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-[95vw] overflow-hidden" data-testid="cascade-close-dialog">
                        <div className="px-5 py-4 border-b bg-amber-50">
                            <h3 className="text-sm font-semibold text-amber-900">Fermer les tickets liés ?</h3>
                            <p className="text-xs text-amber-700 mt-1">Ce ticket a {cascadeDialog.openChildren.length} ticket(s) lié(s) encore ouvert(s). Souhaitez-vous les fermer également ?</p>
                        </div>
                        <div className="px-5 py-3 max-h-60 overflow-y-auto space-y-2">
                            {cascadeDialog.openChildren.map((child) => (
                                <label key={child.id} className="flex items-center gap-2.5 p-2 rounded-md border hover:bg-gray-50 cursor-pointer transition-colors" data-testid={`cascade-child-${child.id}`}>
                                    <Checkbox checked={!!cascadeSelected[child.id]} onCheckedChange={(checked) => setCascadeSelected((prev) => ({ ...prev, [child.id]: checked }))} />
                                    <div className="flex-1 min-w-0"><span className="text-xs font-mono text-gray-400 mr-1.5">{child.ticket_number}</span><span className="text-sm text-gray-900">{child.title}</span></div>
                                    <StatusBadge status={child.status} className="!text-[9px] !px-1.5 !py-0" />
                                </label>
                            ))}
                        </div>
                        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={handleCascadeSkip} data-testid="cascade-skip-btn">Résoudre sans fermer</Button>
                            <Button size="sm" onClick={handleCascadeConfirm} disabled={Object.values(cascadeSelected).every((v) => !v)} data-testid="cascade-confirm-btn">Fermer les sélectionnés</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ATTACHMENT PREVIEW MODAL */}
            {previewAtt && (() => {
                const isImage = previewAtt.content_type?.startsWith("image/");
                const isVideo = previewAtt.content_type?.startsWith("video/");
                const isPdf = previewAtt.content_type === "application/pdf";
                const isText = previewAtt.content_type?.startsWith("text/");
                const downloadUrl = api.getAttachmentUrl(previewAtt.id);
                const previewUrl = api.getAttachmentPreviewUrl(previewAtt.id);
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setPreviewAtt(null)} data-testid="attachment-preview-overlay">
                        <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-[95vw] max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="attachment-preview-modal">
                            <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
                                <div className="flex items-center gap-3 min-w-0">
                                    {isImage && <Image className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                                    {isVideo && <Film className="w-4 h-4 text-purple-500 flex-shrink-0" />}
                                    {isPdf && <FileIcon className="w-4 h-4 text-red-500 flex-shrink-0" />}
                                    {!isImage && !isVideo && !isPdf && <FileIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                                    <span className="text-sm font-medium text-gray-900 truncate">{previewAtt.filename}</span>
                                    <span className="text-xs text-gray-400 flex-shrink-0">{formatFileSize(previewAtt.size)}</span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <a href={downloadUrl} download={previewAtt.filename} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" data-testid="preview-download-btn"><Download className="w-3.5 h-3.5" />Télécharger</a>
                                    <button onClick={() => setPreviewAtt(null)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors" data-testid="preview-close-btn"><X className="w-5 h-5" /></button>
                                </div>
                            </div>
                            <div className="flex-1 overflow-auto flex items-center justify-center p-4 bg-gray-100 min-h-[300px]">
                                {isImage && <img src={previewUrl} alt={previewAtt.filename} className="max-w-full max-h-[75vh] object-contain rounded-md shadow-sm" data-testid="preview-image" />}
                                {isVideo && <video src={previewUrl} controls autoPlay className="max-w-full max-h-[75vh] rounded-md shadow-sm" data-testid="preview-video" />}
                                {isPdf && <PdfPreview attachmentId={previewAtt.id} filename={previewAtt.filename} />}
                                {isText && <iframe src={previewUrl} title={previewAtt.filename} className="w-full h-[75vh] rounded-md border bg-white font-mono text-sm" data-testid="preview-text" />}
                                {!isImage && !isVideo && !isPdf && !isText && (
                                    <div className="text-center py-12"><FileIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-sm text-gray-500 mb-1">Aperçu non disponible pour ce type de fichier</p><p className="text-xs text-gray-400 mb-4">{previewAtt.content_type}</p><a href={downloadUrl} download={previewAtt.filename} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors"><Download className="w-4 h-4" />Télécharger le fichier</a></div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* AI REFORMULATION MODAL */}
            {aiResult && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="ai-modal-overlay">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col" data-testid="ai-modal">
                        <div className="px-5 py-4 border-b bg-purple-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Wand2 className="w-5 h-5 text-purple-600" />
                                <h3 className="text-sm font-semibold text-purple-900">Reformulation IA</h3>
                                <span className="text-[10px] text-purple-500 bg-purple-100 px-1.5 py-0.5 rounded">{aiResult.model}</span>
                            </div>
                            <button onClick={() => setAiResult(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Description originale</label>
                                    <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap min-h-[200px]" data-testid="ai-original">
                                        {aiResult.original}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-2 block">Description reformulée</label>
                                    <textarea
                                        value={aiEdited}
                                        onChange={(e) => setAiEdited(e.target.value)}
                                        className="w-full border border-purple-200 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap min-h-[200px] focus:ring-2 focus:ring-purple-300 focus:border-purple-400 resize-y"
                                        data-testid="ai-reformulated"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setAiResult(null)} data-testid="ai-cancel-btn">Annuler</Button>
                            <Button size="sm" onClick={handleApplyReformulation} className="bg-purple-600 hover:bg-purple-700 text-white gap-1.5" data-testid="ai-apply-btn">
                                <Wand2 className="w-3.5 h-3.5" /> Appliquer la reformulation
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRMATION DIALOG */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="delete-confirm-overlay">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-[95vw] overflow-hidden" data-testid="delete-confirm-dialog">
                        <div className="px-5 py-4 border-b bg-red-50">
                            <h3 className="text-sm font-semibold text-red-900">Supprimer le ticket {ticket.ticket_number} ?</h3>
                            <p className="text-xs text-red-700 mt-1">Cette action est irréversible depuis l'interface.</p>
                        </div>
                        <div className="px-5 py-4">
                            <p className="text-sm text-gray-700">Le ticket <strong>{ticket.ticket_number}</strong> — <em>{ticket.title}</em> sera supprimé définitivement de toutes les vues. Seul un administrateur peut le restaurer via l'API.</p>
                        </div>
                        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)} data-testid="delete-cancel-btn">Annuler</Button>
                            <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="delete-confirm-btn" className="bg-red-600 hover:bg-red-700 text-white">Supprimer définitivement</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
